/**
 * Set Password — public page reached via the invite link Codemagic emails
 * to a newly-invited subcontractor. Reads `?token=…` from the URL, POSTs
 * to `/api/auth/set-password`, and redirects the user to `/login` with a
 * one-off "password set" toast so they can sign in.
 *
 * No auth header required — the token IS the credential. Backend validates
 * the JWT purpose (`field_sub_invite`) and 7-day expiry before rotating
 * the user's password hash.
 */
import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, KeyRound } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error("Missing invite token"); return; }
    if (pw1.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pw1 !== pw2) { toast.error("Passwords don't match"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/auth/set-password`, { token, password: pw1 });
      toast.success("Password set. You can now log in.");
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 1400);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to set password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center p-4" data-testid="set-password-page">
      <form onSubmit={submit} className="w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-sm p-6 space-y-4">
        <div className="flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-blue-400" />
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-300">Set Your Password</div>
        </div>
        <h1 className="font-heading text-2xl font-black">Welcome to SealTech Field</h1>
        <p className="text-xs text-zinc-400">
          Pick a password for your phone-app login. Minimum 8 characters. You&apos;ll use this password + your email to sign in on your phone from now on.
        </p>
        {!token && (
          <div className="text-xs text-red-300 bg-red-950/50 border border-red-800 rounded-sm p-2">
            No invite token in the URL. Ask your admin to resend the invite link.
          </div>
        )}
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1">Password</label>
          <input
            type="password"
            value={pw1}
            onChange={(e) => setPw1(e.target.value)}
            className="w-full h-11 px-3 bg-zinc-950 border border-zinc-700 text-white rounded-sm focus:outline-none focus:border-blue-500"
            data-testid="set-password-pw1"
            autoFocus
            required
            minLength={8}
          />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1">Confirm password</label>
          <input
            type="password"
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            className="w-full h-11 px-3 bg-zinc-950 border border-zinc-700 text-white rounded-sm focus:outline-none focus:border-blue-500"
            data-testid="set-password-pw2"
            required
            minLength={8}
          />
        </div>
        <button
          type="submit"
          disabled={saving || done || !token}
          className="w-full h-11 bg-blue-700 hover:bg-blue-800 disabled:opacity-40 text-white text-sm font-bold uppercase tracking-wider rounded-sm inline-flex items-center justify-center gap-2"
          data-testid="set-password-submit"
        >
          {done ? (<><CheckCircle2 className="w-4 h-4" /> Password set</>) : (saving ? "Setting…" : "Set Password & Continue")}
        </button>
      </form>
    </div>
  );
}
